/**
 * Results Types - Tipos TypeScript para tela de resultados
 * @module features/results/types
 *
 * Tipos para a feature de resultados do assessment,
 * incluindo estados do relatorio e dados de preview.
 *
 * T001-FE: Tela de Resumo com Resultados do Assessment
 */

// =============================================================================
// REPORT STATUS TYPES
// =============================================================================

/**
 * Status possíveis do relatorio
 * - loading: Verificando status do relatorio
 * - generating: Relatorio sendo gerado em background
 * - ready: Relatorio disponivel para download
 * - error: Falha na geracao do relatorio
 * - unavailable: Endpoint nao disponivel (graceful degradation)
 */
export type ReportStatus = 'loading' | 'generating' | 'ready' | 'error' | 'unavailable';

/**
 * Score de uma dimensao individual
 */
export interface DimensionScoreData {
  /** Nome da dimensao */
  name: string;
  /** Score da dimensao (0-100) */
  score: number;
}

/**
 * Dados de scores do relatorio
 */
export interface ScoresData {
  /** Score geral (0-100) */
  overall: number;
  /** Scores por dimensao */
  dimensions: DimensionScoreData[];
}

/**
 * Metadados do relatorio gerado
 */
export interface ReportMetadata {
  /** Score geral do assessment (0-100) */
  overallScore?: number;
  /** Tempo de geracao em ms */
  generationTimeMs?: number;
  /** Scores por dimensao */
  dimensions?: Record<string, number>;
}

/**
 * Estado do relatorio
 */
export interface ReportState {
  /** Status atual do relatorio */
  status: ReportStatus;
  /** URL do relatorio para download (quando ready) */
  reportUrl: string | null;
  /** Mensagem de erro (quando error) */
  error: string | null;
  /** Metadados do relatorio */
  metadata: ReportMetadata | null;
}

// =============================================================================
// ASSESSMENT SUMMARY TYPES
// =============================================================================

/**
 * Tipo de assessment
 */
export type AssessmentType = 'tech' | 'governance';

/**
 * Resumo do assessment completado
 */
export interface AssessmentSummary {
  /** Nome do usuario */
  userName: string;
  /** Tipo de assessment realizado */
  assessmentType: AssessmentType;
  /** Data/hora de conclusao */
  completedAt: Date;
  /** ID da sessao */
  sessionId: string;
  /** Numero de perguntas respondidas (opcional) */
  questionsAnswered?: number;
  /** Duracao em minutos (opcional) */
  durationMinutes?: number;
}

// =============================================================================
// COMPONENT PROPS TYPES
// =============================================================================

/**
 * Props para SuccessView component
 */
export interface SuccessViewProps {
  /** Resumo do assessment */
  summary: AssessmentSummary;
  /** URL do relatorio para download */
  reportUrl: string;
  /** Handler para download do relatorio */
  onDownload: () => void;
  /** Handler para iniciar novo assessment */
  onNewAssessment: () => void;
  /** URL para agendar conversa com especialista (opcional) */
  specialistBookingUrl?: string;
  /** Se o download esta em progresso */
  isDownloading?: boolean;
  /** Dados de scores (opcional) */
  scores?: ScoresData | null;
  /** Insight/citacao destacada (opcional) */
  insight?: string | null;
  /** Se o PDF esta pronto para download (FIX-026) */
  pdfReady?: boolean;
}

/**
 * Props para LoadingView component
 */
export interface LoadingViewProps {
  /** Mensagem customizada (opcional) */
  message?: string;
}

/**
 * Props para ErrorView component
 */
export interface ErrorViewProps {
  /** Mensagem de erro */
  error: string;
  /** Handler para retry */
  onRetry: () => void;
  /** Handler para iniciar novo assessment */
  onNewAssessment: () => void;
}

/**
 * Props para AssessmentPreview component
 */
export interface AssessmentPreviewProps {
  /** Resumo do assessment */
  summary: AssessmentSummary;
}

// =============================================================================
// HOOK RETURN TYPES
// =============================================================================

/**
 * Retorno do hook useReportStatus
 */
export interface UseReportStatusReturn {
  /** Estado atual do relatorio */
  status: ReportStatus;
  /** URL do relatorio (quando disponivel) */
  reportUrl: string | null;
  /** Mensagem de erro (quando falhou) */
  error: string | null;
  /** Metadados do relatorio */
  metadata: ReportMetadata | null;
  /** Dados de scores (quando ready) */
  scores: ScoresData | null;
  /** Insight/citacao destacada (quando ready) */
  insight: string | null;
  /** Se esta carregando/gerando */
  isLoading: boolean;
  /** Se esta pronto para download */
  isReady: boolean;
  /** Funcao para fazer retry */
  retry: () => void;
  /** Funcao para download do relatorio */
  download: () => Promise<void>;
  /** Se o download esta em progresso */
  isDownloading: boolean;
  /** Se o PDF esta pronto para download (FIX-026) */
  pdfReady: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * URL padrao para agendar conversa com especialistas BossaBox
 */
export const DEFAULT_SPECIALIST_URL = 'https://bossabox.com';

/**
 * Intervalo de polling em ms (2 segundos)
 * FIX-004: Reduzido de 3s para 2s para UX mais responsiva
 */
export const POLLING_INTERVAL_MS = 2000;

/**
 * Maximo de tentativas de polling
 * FIX-004: Aumentado de 10 para 30 (60 segundos total)
 * para acomodar geracao de relatorio que pode demorar
 * FIX-014: Aumentado de 30 para 60 (120 segundos total)
 * Backend processa relatorio em background (ate 90s timeout)
 * 60 tentativas x 2s = 120s de cobertura total
 */
export const MAX_POLLING_ATTEMPTS = 60;

/**
 * Timeout total do polling em ms (30 segundos)
 */
export const POLLING_TIMEOUT_MS = 30000;
